import PropTypes from 'prop-types';
import React, { useCallback, useMemo, memo } from 'react';
import classNames from 'classnames';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import { useMakeSelector } from 'hooks/use-make-selector';
import useTrolleyActions from 'hooks/use-trolley-actions';

import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import Button from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import { Close } from '@johnlewispartnership/wtr-ingredients/foundations/icons';

import Thumbnail from 'components/Images/Thumbnail';
import TrolleyControls from 'components/TrolleyActions/TrolleyControlsV2';
// Dual .js and .d.ts cannot be resolved
// eslint-disable-next-line import-x/extensions
import ProductSize from 'components/Product/Size';
import ProductPrice from 'components/Product/Price';
import ProductTags from 'components/ProductTags';
import PromotionElement from 'components/Product/Promotion';
import { TROLLEY_TEXT } from 'components/ProductPod/TrolleyControls/constants';
import Null from 'components/Null';
import { SSRMediaQuery } from 'components/SSRMediaQuery';
import { ALLERGEN_UPDATE_PRODUCT_TAG_NAME } from 'components/ProductPod2/utils';

import removeItemFromTrolley from 'redux/modules/trolley/actions/remove-item-from-trolley';
import fetchNextUnavailableTrolleyItemSubs from 'redux/modules/trolley/actions/fetch-next-unavailable-trolley-item-subs';
import { makeGetProductByLineNumber } from 'redux/modules/entities/selectors/products';
import {
  getIsRedPriceProduct,
  makeGetPromotionsByIds,
} from 'redux/modules/entities/selectors/promotions';
import { getPDPUrl } from 'utils/product';
import { getClickContext } from 'utils/clickContext';
import { handleProductListClick } from 'utils/gtm';
import { weightOptions } from 'constants/weightOptions';
import { BREAKPOINTS } from 'constants/grid';
import { getProductConflictById } from 'redux/modules/conflicts/selectors';

import styles from './index.scss';

const EMPTY_ARRAY = [];

const PodButtons = memo(
  ({
    isRemovable,
    isUnavailable,
    lineNumber,
    optimisticTrolleyQuantityAmount,
    productName,
    onClickAdd,
    onClickRemove,
    productPodAddText = 'Select',
  }) => {
    if (isRemovable) {
      return (
        <div className={styles.removeBtnContainer}>
          <Button
            data-testid="remove-btn"
            aria-label={`Remove ${productName} from trolley`}
            startIcon={<Close size="small" />}
            onClick={onClickRemove}
            theme="primaryWhite"
            width="fit"
          />
        </div>
      );
    }
    if (!isUnavailable) {
      if (optimisticTrolleyQuantityAmount > 0) {
        return (
          <div className={styles.trolleyControllsContainer}>
            <TrolleyControls lineNumber={lineNumber} productName={productName} />
          </div>
        );
      }

      return (
        <div className={styles.trolleyControllsContainer}>
          <Button
            data-testid="swap-btn"
            label={productPodAddText}
            onClick={onClickAdd}
            width="full"
          />
        </div>
      );
    }
    return null;
  },
);

PodButtons.displayName = 'PodButtons';

PodButtons.propTypes = {
  lineNumber: PropTypes.string,
  isRemovable: PropTypes.bool,
  isUnavailable: PropTypes.bool,
  optimisticTrolleyQuantityAmount: PropTypes.number,
  onClickAdd: PropTypes.func,
  onClickRemove: PropTypes.func,
  productName: PropTypes.string,
  productPodAddText: PropTypes.string,
};

const SubProductPod = ({
  lineNumber = null,
  isUnavailable = false,
  isRemovable = false,
  trolleyItemId = null,
  productPodAddText = 'Select',
  listDefinitions,
}) => {
  const dispatch = useWtrDispatch();
  const {
    brand,
    brandName,
    categories,
    currentSaleUnitPrice,
    displayPriceEstimated,
    displayPriceQualifier,
    id: productId,
    name: productName,
    productTags,
    productType,
    promotions,
    displayPrice,
  } = useMakeSelector(makeGetProductByLineNumber, lineNumber) ?? {};
  const productPromotions = useMakeSelector(makeGetPromotionsByIds, promotions);
  const redOffer = useWtrSelector(state => getIsRedPriceProduct(state, productId));
  const {
    desiredAmount,
    desiredUom,
    addToTrolley,
    optimisticTrolleyQuantityAmount,
    optimisticTrolleyQuantityUom,
  } = useTrolleyActions({ lineNumber, productId });

  const conflict = useWtrSelector(state => getProductConflictById(state, productId));

  const uomDisplay = weightOptions[optimisticTrolleyQuantityUom]?.label ?? '';

  const allergenTags =
    productTags?.filter(tag => tag.name === ALLERGEN_UPDATE_PRODUCT_TAG_NAME) ?? EMPTY_ARRAY;

  const pdpUrl = useMemo(() => getPDPUrl(productId, productName), [productId, productName]);
  const OptionalLink = pdpUrl ? 'a' : Null;

  const productLinkHandler = useCallback(() => {
    handleProductListClick({
      productData: {
        brand: brand || brandName,
        categories,
        currentSaleUnitPrice,
        id: productId,
        name: productName,
        productType,
        displayPrice,
      },
      conflictMessage: conflict?.messages?.shortItem,
      promotions: productPromotions,
      podType: 'regular',
      listDefinitions,
    });
  }, [
    productId,
    productName,
    productPromotions,
    productType,
    currentSaleUnitPrice,
    categories,
    brand,
    brandName,
    conflict?.messages?.shortItem,
    displayPrice,
    listDefinitions,
  ]);

  const onClickRemove = useCallback(() => {
    dispatch(fetchNextUnavailableTrolleyItemSubs(lineNumber));
    dispatch(removeItemFromTrolley({ trolleyItemId }));
  }, [lineNumber, trolleyItemId, dispatch]);

  const onClickAdd = useCallback(
    event => {
      addToTrolley({
        quantity: {
          amount: desiredAmount,
          uom: desiredUom,
        },
        clickContext: getClickContext(event),
      });
    },
    [addToTrolley, desiredAmount, desiredUom],
  );

  if (!productId) return null;

  return (
    <li
      className={classNames(styles.product, {
        [styles.unavailableProduct]: isUnavailable,
      })}
      key={lineNumber}
      data-test-trolley-pod-name={productName}
    >
      <div
        className={classNames(styles.productInfo, {
          [styles.unavailableProductInfo]: isUnavailable,
        })}
      >
        <div className={styles.thumbnailWrapper} data-testid="thumbnailWrapper">
          <OptionalLink
            data-testid="link-pdp"
            href={pdpUrl}
            aria-label={productName}
            target="_blank"
            rel="noreferrer"
            onClick={productLinkHandler}
          >
            <Thumbnail itemId={productId} name={productName} disableTooltip />
          </OptionalLink>
        </div>
        <div className={styles.infoContainer}>
          <ProductTags productTags={allergenTags} />
          <OptionalLink
            className={styles.titleLink}
            data-testid="link-pdp-title"
            href={pdpUrl}
            target="_blank"
            rel="noreferrer"
            onClick={productLinkHandler}
          >
            <Typography
              styleAs="paragraphHeading"
              textStyle="heading-l"
              className={styles.productTitle}
              data-testid="product-name"
            >
              {productName}
              <ProductSize productId={productId} additionalClasses={styles.typicalWeight} />
            </Typography>
          </OptionalLink>
          <SSRMediaQuery maxWidth={BREAKPOINTS.lg}>
            {promotions?.length > 0 && (
              <div className={styles.promotions} data-test="trolley-pod-promotion">
                {promotions.map(promotionId => (
                  <PromotionElement
                    key={`${productId}_${promotionId}`}
                    {...{
                      productId,
                      promotionId,
                      showMyWaitrose: true,
                      isTrolley: true,
                      isSubPod: true,
                    }}
                  />
                ))}
              </div>
            )}
            <div
              data-testid="product-pod-prices-section"
              className={classNames(styles.prices, {
                [styles.unavailablePrice]: isUnavailable,
              })}
            >
              <span
                data-testid="product-pod-price"
                className={classNames(styles.itemPriceSection, {
                  [styles.redText]: redOffer,
                })}
              >
                <span className="sr-only">{TROLLEY_TEXT.itemPrice}</span>
                <Typography
                  styleAs="sectionHeading"
                  textStyle="heading-xl"
                  className={styles.price}
                  fixedSize
                >
                  <ProductPrice productId={productId} />
                  {displayPriceEstimated && (
                    <span className={styles.priceEst}>
                      {` ${TROLLEY_TEXT.each}`}
                      <abbr title="estimated">{` ${TROLLEY_TEXT.estimated}`}</abbr>
                    </span>
                  )}
                </Typography>
                {displayPriceQualifier && (
                  <>
                    <span className="sr-only">{TROLLEY_TEXT.pricePer}</span>
                    <Typography
                      styleAs="paragraph"
                      textStyle="body-m"
                      className={styles.pricePerUnit}
                    >
                      {/* removing parenthesis temporary */}
                      {displayPriceQualifier.split(/[()]/).join('')}
                    </Typography>
                  </>
                )}
              </span>

              {isUnavailable && (
                <Typography styleAs="paragraph" textStyle="body-m" noMargins>
                  {`${TROLLEY_TEXT.qty}: ${optimisticTrolleyQuantityAmount}${uomDisplay}`}
                </Typography>
              )}
            </div>
          </SSRMediaQuery>
          <SSRMediaQuery minWidth={BREAKPOINTS.lg}>
            {promotions?.length > 0 && (
              <div className={styles.promotions} data-test="trolley-pod-promotion">
                {promotions.map(promotionId => (
                  <PromotionElement
                    key={`${productId}_${promotionId}`}
                    {...{
                      productId,
                      promotionId,
                      showMyWaitrose: true,
                      isTrolley: true,
                      isSubPod: true,
                    }}
                  />
                ))}
              </div>
            )}
          </SSRMediaQuery>
        </div>
      </div>
      <div className={styles.actionContainer}>
        <SSRMediaQuery minWidth={BREAKPOINTS.lg}>
          <div
            data-testid="product-pod-prices-section"
            className={classNames(styles.prices, {
              [styles.unavailablePrice]: isUnavailable,
            })}
          >
            <div
              data-testid="product-pod-price"
              className={classNames(styles.itemPriceSection, {
                [styles.redText]: redOffer,
              })}
            >
              <span className="sr-only">{TROLLEY_TEXT.itemPrice}</span>
              <Typography
                styleAs="sectionHeading"
                textStyle="heading-xl"
                className={styles.price}
                fixedSize
              >
                <ProductPrice productId={productId} />
                {displayPriceEstimated && (
                  <span className={styles.priceEst}>
                    {` ${TROLLEY_TEXT.each}`}
                    <abbr title="estimated">{` ${TROLLEY_TEXT.estimated}`}</abbr>
                  </span>
                )}
              </Typography>
              {displayPriceQualifier && (
                <>
                  <span className="sr-only">{TROLLEY_TEXT.pricePer}</span>
                  <Typography
                    styleAs="paragraph"
                    textStyle="body-m"
                    className={styles.pricePerUnit}
                  >
                    {/* removing parenthesis temporary */}
                    {displayPriceQualifier.split(/[()]/).join('')}
                  </Typography>
                </>
              )}
            </div>

            {isUnavailable && (
              <Typography styleAs="paragraphHeading" textStyle="heading-l" noMargins>
                {`${TROLLEY_TEXT.qty}: ${optimisticTrolleyQuantityAmount}${uomDisplay}`}
              </Typography>
            )}
          </div>
        </SSRMediaQuery>
        <PodButtons
          {...{
            isRemovable,
            isUnavailable,
            lineNumber,
            optimisticTrolleyQuantityAmount,
            productName,
            onClickAdd,
            onClickRemove,
            productPodAddText,
          }}
        />
      </div>
    </li>
  );
};

SubProductPod.displayName = 'SubProductPod';

SubProductPod.propTypes = {
  lineNumber: PropTypes.string,
  isRemovable: PropTypes.bool,
  isUnavailable: PropTypes.bool,
  trolleyItemId: PropTypes.number,
  productPodAddText: PropTypes.string,
  listDefinitions: PropTypes.shape({
    curation: PropTypes.string,
    format: PropTypes.string,
    page: PropTypes.string,
    title: PropTypes.string,
  }),
};

export default memo(SubProductPod);
